import * as React from 'react'
import { cn } from '@/lib/utils'
import { Icon } from '@/design-system/atoms/Icon'
import { Spinner } from '@/design-system/atoms/Spinner'
import { Checkbox } from '@/design-system/atoms/Checkbox'
import { EmptyState } from '@/design-system/molecules/EmptyState'

export type SortDirection = 'asc' | 'desc' | null

export interface DataTableColumn<T> {
  key: string
  header: React.ReactNode
  accessor?: (row: T) => React.ReactNode
  sortable?: boolean
  width?: string | number
  align?: 'left' | 'center' | 'right'
  className?: string
  headerClassName?: string
}

export interface DataTableProps<
  T,
> extends React.HTMLAttributes<HTMLDivElement> {
  columns: DataTableColumn<T>[]
  data: T[]
  rowKey: (row: T) => string | number
  loading?: boolean
  emptyMessage?: string
  emptyIconName?: React.ComponentProps<typeof EmptyState>['iconName']
  onRowClick?: (row: T) => void

  /** Controlled sort state */
  sortBy?: string
  sortDirection?: SortDirection
  onSortChange?: (key: string, direction: SortDirection) => void

  /** Selection */
  selectable?: boolean
  selectedKeys?: Array<string | number>
  onSelectionChange?: (keys: Array<string | number>) => void

  dense?: boolean
}

function toggleSort(current: SortDirection): SortDirection {
  if (current === null) return 'asc'
  if (current === 'asc') return 'desc'
  return null
}

export function DataTable<T>({
  className,
  columns,
  data,
  rowKey,
  loading,
  emptyMessage = 'Nenhum registro encontrado',
  emptyIconName = 'search',
  onRowClick,
  sortBy,
  sortDirection,
  onSortChange,
  selectable,
  selectedKeys = [],
  onSelectionChange,
  dense,
  // O rótulo acessível pertence à <table>, não ao <div> que a envolve: em
  // `...rest` no wrapper, `getByRole('table', { name })` não encontra nada e o
  // leitor de tela anuncia a tabela sem nome.
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  ...rest
}: DataTableProps<T>) {
  const allSelected = data.length > 0 && selectedKeys.length === data.length
  const someSelected = selectedKeys.length > 0 && !allSelected

  const toggleAll = () => {
    if (!onSelectionChange) return
    if (allSelected) onSelectionChange([])
    else onSelectionChange(data.map(rowKey))
  }

  const toggleRow = (key: string | number) => {
    if (!onSelectionChange) return
    onSelectionChange(
      selectedKeys.includes(key)
        ? selectedKeys.filter(k => k !== key)
        : [...selectedKeys, key]
    )
  }

  const handleSort = (col: DataTableColumn<T>) => {
    if (!col.sortable || !onSortChange) return
    const currentForCol = sortBy === col.key ? (sortDirection ?? null) : null
    onSortChange(col.key, toggleSort(currentForCol))
  }

  return (
    <div
      className={cn(
        'overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900',
        className
      )}
      {...rest}
    >
      <div className="overflow-x-auto">
        <table
          className="w-full text-sm"
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
        >
          <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-400">
            <tr>
              {selectable && (
                <th scope="col" className="w-10 px-3 py-2">
                  <Checkbox
                    aria-label={
                      allSelected ? 'Desmarcar todos' : 'Selecionar todos'
                    }
                    checked={allSelected}
                    ref={el => {
                      if (el) el.indeterminate = someSelected
                    }}
                    onChange={toggleAll}
                  />
                </th>
              )}
              {columns.map(col => {
                const active = sortBy === col.key
                const dir = active ? sortDirection : null
                return (
                  <th
                    key={col.key}
                    scope="col"
                    style={col.width ? { width: col.width } : undefined}
                    className={cn(
                      'px-3 py-2.5',
                      col.align === 'right' && 'text-right',
                      col.align === 'center' && 'text-center',
                      col.headerClassName
                    )}
                    aria-sort={
                      active
                        ? dir === 'asc'
                          ? 'ascending'
                          : dir === 'desc'
                            ? 'descending'
                            : 'none'
                        : col.sortable
                          ? 'none'
                          : undefined
                    }
                  >
                    {col.sortable ? (
                      <button
                        type="button"
                        onClick={() => handleSort(col)}
                        className="inline-flex items-center gap-1 rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 hover:text-gray-900 dark:hover:text-gray-200"
                      >
                        {col.header}
                        <Icon
                          name={
                            active && dir === 'asc'
                              ? 'arrow-up'
                              : active && dir === 'desc'
                                ? 'arrow-down'
                                : 'chevron-up'
                          }
                          size={12}
                          className={cn(
                            active
                              ? 'text-primary-500'
                              : 'text-gray-400 opacity-50'
                          )}
                        />
                      </button>
                    ) : (
                      col.header
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
            {loading ? (
              <tr>
                <td
                  colSpan={columns.length + (selectable ? 1 : 0)}
                  className="px-3 py-10 text-center"
                >
                  <Spinner size="md" className="mx-auto text-primary-500" />
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (selectable ? 1 : 0)}
                  className="px-3 py-6"
                >
                  <EmptyState
                    size="sm"
                    iconName={emptyIconName}
                    title={emptyMessage}
                  />
                </td>
              </tr>
            ) : (
              data.map(row => {
                const key = rowKey(row)
                const selected = selectedKeys.includes(key)
                return (
                  <tr
                    key={key}
                    // Linha clicável é um controle: sem `tabIndex`/`onKeyDown`,
                    // uma tabela cuja única forma de abrir o item é o clique
                    // fica inacessível por teclado (A11Y-KBD-01).
                    // `tabIndex` + `onKeyDown` bastam para o teclado. `role="button"`
                    // aqui destruiria o papel `row`: as células virariam órfãs, o
                    // leitor de tela pararia de associar cabeçalho↔célula e o nome
                    // acessível seria a concatenação de todas as colunas.
                    tabIndex={onRowClick ? 0 : undefined}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    onKeyDown={
                      onRowClick
                        ? event => {
                            if (event.key !== 'Enter' && event.key !== ' ')
                              return
                            event.preventDefault()
                            onRowClick(row)
                          }
                        : undefined
                    }
                    className={cn(
                      'text-gray-800 transition-colors dark:text-gray-200',
                      onRowClick &&
                        'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800',
                      selected && 'bg-primary-50 dark:bg-primary-900/20'
                    )}
                    aria-selected={selectable ? selected : undefined}
                  >
                    {selectable && (
                      <td
                        className="px-3 py-2"
                        onClick={e => e.stopPropagation()}
                      >
                        <Checkbox
                          aria-label={
                            selected ? 'Desmarcar linha' : 'Selecionar linha'
                          }
                          checked={selected}
                          onChange={() => toggleRow(key)}
                        />
                      </td>
                    )}
                    {columns.map(col => (
                      <td
                        key={col.key}
                        className={cn(
                          dense ? 'px-3 py-1.5' : 'px-3 py-2.5',
                          col.align === 'right' && 'text-right',
                          col.align === 'center' && 'text-center',
                          col.className
                        )}
                      >
                        {col.accessor
                          ? col.accessor(row)
                          : (row as Record<string, React.ReactNode>)[col.key]}
                      </td>
                    ))}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

DataTable.displayName = 'DataTable'
